// Copyright (c) Wiens Consulting. All rights reserved.

import { useState, useEffect, useCallback } from "react"
import { useParams, Link } from "react-router-dom"
import {
  IconArrowLeft,
  IconArrowUp,
  IconArrowDown,
  IconChevronDown,
  IconChevronRight,
  IconLoader2,
  IconPlus,
  IconDeviceFloppy,
  IconSettings,
  IconCash,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { api } from "@/lib/api"
import { hasPermission } from "@/store/permissionsSlice"
import { useAppSelector } from "@/store"
import { ProgramSettingsEditor } from "./program-settings-editor"
import { formatProgramStartDate } from "./program-settings"
import { pseEnergyTypeLabels } from "@/lib/benefit-enums"
import { BenefitEditScope } from "@/components/benefit/benefit-edit-scope"
import { toast } from "sonner"

// ─── Types ────────────────────────────────────────────────────────────────────

interface BenefitProgramDetail {
  id: number
  name: string
  description: string
  availableProgramYears: number[]
}

export interface ProgramYearSettings {
  programYear: number
  revision: number
  startDate: string
  calculationConstants: {
    coefficientA: number
    coefficientB: number
    coefficientC: number
    maximumBenefit: number
    minimumBenefit: number
  }
  eligibilityGuidelines: {
    householdSize: number
    monthlyIncomeLimit: number
  }[]
  householdSizeAdjusters: {
    numberOfPeople: number
    adjustmentAmount: number
  }[]
  housingTypeAdjusters: {
    housingType: number
    adjustmentAmount: number
  }[]
  backupEnergyCosts: {
    energySourceId: number
    energySourceName: string
    amount: number
  }[]
  fundingSources: ProgramFundingSource[]
}

interface ProgramFundingSource {
  fundingSourceId: number
  name: string
  contractNumber: string | null
  isActive: boolean
  sortOrder: number
  effectiveDate: string
  totalBudget: number
  totalDisbursed: number
  remaining: number
}

interface UpdateProgramFundingSourcesRequest {
  programYear: number
  fundingSources: {
    fundingSourceId: number
    sortOrder: number
    effectiveDate: string
  }[]
}

interface FundingSourceOption {
  id: number
  name: string
  contractNumber: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

const housingTypeNames: Record<number, string> = {
  0: "1-3 Family",
  1: "4+ Family",
  2: "Hi-Rise",
  3: "Mobile",
  4: "RV",
  5: "RV w/ Propane",
  255: "Unknown (legacy)",
}

function formatNumber(n: number, decimals = 4): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function BudgetBar({ disbursed, total }: { disbursed: number; total: number }) {
  if (total === 0) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  const pct = Math.min(100, (disbursed / total) * 100)
  const isOverBudget = disbursed > total
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isOverBudget ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-green-500"}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{pct.toFixed(0)}%</span>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BenefitProgramDetailPage() {
  return <BenefitEditScope><BenefitProgramDetailContent /></BenefitEditScope>
}

function BenefitProgramDetailContent() {
  const { id } = useParams<{ id: string }>()
  const [program, setProgram] = useState<BenefitProgramDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createYearDialogOpen, setCreateYearDialogOpen] = useState(false)
  const [createYear, setCreateYear] = useState("")
  const [creatingYear, setCreatingYear] = useState<number | null>(null)
  const permissions = useAppSelector((state) => state.permissions.permissions)
  const canEditSettings = hasPermission(permissions, "admin:manage-access")

  const reloadProgram = async () => {
    if (!id) return
    const data = await api.get<BenefitProgramDetail>(`/api/benefit-programs/${id}`)
    setProgram(data)
  }

  useEffect(() => {
    if (!id) return
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        setError(null)
        const data = await api.get<BenefitProgramDetail>(`/api/benefit-programs/${id}`)
        if (!cancelled) setProgram(data)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load program")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [id])

  if (loading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6 max-w-5xl">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !program) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Link to="/benefit-programs">
          <Button variant="ghost" size="sm">
            <IconArrowLeft className="size-4 mr-1.5" />
            Back to Programs
          </Button>
        </Link>
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
          {error ?? "Program not found."}
        </div>
      </div>
    )
  }

  const sortedYears = [...program.availableProgramYears].sort((a, b) => b - a)
  const supportsSettingsEditing = [0, 1, 3].includes(program.id)

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6 max-w-5xl">
      {/* Back link + header */}
      <div>
        <Link to="/benefit-programs">
          <Button variant="ghost" size="sm" className="mb-2">
            <IconArrowLeft className="size-4 mr-1.5" />
            Back to Programs
          </Button>
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{program.name}</h1>
            <p className="text-muted-foreground mt-1">{program.description}</p>
          </div>
          {canEditSettings && supportsSettingsEditing && (
            <Dialog open={createYearDialogOpen} onOpenChange={setCreateYearDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <IconPlus className="mr-1.5 size-4" />
                  Add program year
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add program year</DialogTitle>
                  <DialogDescription>Choose the program year before entering its approved constants and guidelines.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-2">
                  <Label htmlFor="new-program-year">Program year</Label>
                  <Input id="new-program-year" type="number" min="1900" max="9999" step="1" value={createYear} onChange={(event) => setCreateYear(event.target.value)} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateYearDialogOpen(false)}>Cancel</Button>
                  <Button onClick={() => {
                    const parsed = Number(createYear)
                    if (Number.isInteger(parsed) && parsed >= 1900 && parsed <= 9999) {
                      setCreatingYear(parsed)
                      setCreateYearDialogOpen(false)
                    } else {
                      toast.error("Program year must be a whole number from 1900 through 9999")
                    }
                  }}>Continue</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Year Groups */}
      {sortedYears.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No program years configured.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sortedYears.map((year, index) => (
            <ProgramYearGroup
              key={year}
              programId={program.id}
              year={year}
              defaultOpen={index === 0}
              canEdit={canEditSettings && supportsSettingsEditing}
              supportsSettingsEditing={supportsSettingsEditing}
            />
          ))}
        </div>
      )}
      {creatingYear !== null && (
        <ProgramSettingsEditor
          programId={program.id}
          programYear={creatingYear}
          settings={null}
          canEdit={canEditSettings}
          open
          onOpenChange={(open) => { if (!open) setCreatingYear(null) }}
          onSaved={async () => {
            setCreatingYear(null)
            await reloadProgram()
          }}
        />
      )}
    </div>
  )
}

// ─── Program Year Group ───────────────────────────────────────────────────────

function ProgramYearGroup({
  programId,
  year,
  defaultOpen,
  canEdit,
  supportsSettingsEditing,
}: {
  programId: number
  year: number
  defaultOpen: boolean
  canEdit: boolean
  supportsSettingsEditing: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [settings, setSettings] = useState<ProgramYearSettings | null>(null)
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [fetched, setFetched] = useState(false)

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true)
    setSettingsError(null)
    try {
      const data = await api.get<ProgramYearSettings>(
        `/api/benefit-programs/${programId}/settings?programYear=${year}`
      )
      setSettings(data)
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : "Failed to load settings")
      setSettings(null)
    } finally {
      setSettingsLoading(false)
      setFetched(true)
    }
  }, [programId, year])

  useEffect(() => {
    if (!fetched) {
      loadSettings()
    }
  }, [fetched, loadSettings])

  const handleFundingSourcesUpdated = async () => {
    await loadSettings()
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="py-0">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none py-4 hover:bg-muted/50 transition-colors rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {open
                  ? <IconChevronDown className="size-5 text-muted-foreground" />
                  : <IconChevronRight className="size-5 text-muted-foreground" />
                }
                <span className="text-lg font-semibold">{year}</span>
                {settings && (
                  <span className="text-xs text-muted-foreground">
                    Revision {settings.revision} · Start {formatProgramStartDate(settings.startDate)}
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <Separator />
          <CardContent className="py-4">
            {settingsLoading ? (
              <div className="flex items-center gap-2 py-6 text-muted-foreground">
                <IconLoader2 className="size-4 animate-spin" />
                Loading settings…
              </div>
            ) : settingsError ? (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
                {settingsError}
              </div>
            ) : settings ? (
              <div className="space-y-6">
                <FundingSourcesSection
                  programId={programId}
                  programYear={year}
                  fundingSources={settings.fundingSources}
                  onUpdated={handleFundingSourcesUpdated}
                />
                <Separator />
                <ProgramSettingsSection
                  programId={programId}
                  programYear={year}
                  settings={settings}
                  canEdit={canEdit}
                  supportsSettingsEditing={supportsSettingsEditing}
                  onSaved={handleFundingSourcesUpdated}
                />
              </div>
            ) : null}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

// ─── Program Settings Section ─────────────────────────────────────────────────

function ProgramSettingsSection({
  programId,
  programYear,
  settings,
  canEdit,
  supportsSettingsEditing,
  onSaved,
}: {
  programId: number
  programYear: number
  settings: ProgramYearSettings
  canEdit: boolean
  supportsSettingsEditing: boolean
  onSaved: () => void
}) {
  const hasConstants = settings.calculationConstants != null
  const hasEligibility = settings.eligibilityGuidelines.length > 0
  const hasHouseholdAdjusters = settings.householdSizeAdjusters.length > 0
  const hasHousingAdjusters = settings.housingTypeAdjusters.length > 0
  const hasBackupCosts = settings.backupEnergyCosts.length > 0

  const hasAnySettings = hasConstants || hasEligibility || hasHouseholdAdjusters || hasHousingAdjusters || hasBackupCosts

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconSettings className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Program Settings</h4>
        </div>
        {supportsSettingsEditing && <ProgramSettingsEditor
          programId={programId}
          programYear={programYear}
          settings={settings}
          canEdit={canEdit}
          onSaved={onSaved}
        />}
      </div>

      {!hasAnySettings ? (
        <p className="text-sm text-muted-foreground py-4">
          No program settings configured for this year.
        </p>
      ) : (
        <div className="space-y-6">
          {/* Calculation Constants */}
          {hasConstants && (
            <section>
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Calculation Constants
              </h5>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: "Coefficient A", value: formatNumber(settings.calculationConstants.coefficientA) },
                  { label: "Coefficient B", value: formatNumber(settings.calculationConstants.coefficientB) },
                  { label: "Coefficient C", value: formatNumber(settings.calculationConstants.coefficientC) },
                  { label: "Maximum Benefit", value: formatCurrency(settings.calculationConstants.maximumBenefit) },
                  { label: "Minimum Benefit", value: formatCurrency(settings.calculationConstants.minimumBenefit) },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border p-3">
                    <div className="text-xs text-muted-foreground">{item.label}</div>
                    <div className="text-sm font-mono font-medium mt-0.5">{item.value}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Eligibility Guidelines & Household Size Adjusters */}
          {(hasEligibility || hasHouseholdAdjusters) && (
            <section>
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Eligibility Guidelines
              </h5>
              <div className="rounded-xl border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Household Size</TableHead>
                      {hasEligibility && <TableHead className="text-right">Monthly Income Limit</TableHead>}
                      {hasHouseholdAdjusters && <TableHead className="text-right">Size Adjustment</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const guidelineMap = new Map(settings.eligibilityGuidelines.map((g) => [g.householdSize, g]))
                      const adjusterMap = new Map(settings.householdSizeAdjusters.map((a) => [a.numberOfPeople, a]))
                      const allSizes = [...new Set([...guidelineMap.keys(), ...adjusterMap.keys()])].sort((a, b) => a - b)
                      return allSizes.map((size) => {
                        const guideline = guidelineMap.get(size)
                        const adjuster = adjusterMap.get(size)
                        return (
                          <TableRow key={size}>
                            <TableCell>{size} {size === 1 ? "person" : "people"}</TableCell>
                            {hasEligibility && (
                              <TableCell className="text-right font-mono">
                                {guideline ? formatCurrency(guideline.monthlyIncomeLimit) : "—"}
                              </TableCell>
                            )}
                            {hasHouseholdAdjusters && (
                              <TableCell className="text-right font-mono">
                                {adjuster ? formatNumber(adjuster.adjustmentAmount, 2) : "—"}
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      })
                    })()}
                  </TableBody>
                </Table>
              </div>
            </section>
          )}

          {/* Housing Type Adjusters & Backup Energy Costs (side by side) */}
          {(hasHousingAdjusters || hasBackupCosts) && (
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Housing Type Adjusters */}
              {hasHousingAdjusters && (
                <section>
                  <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Housing Type Adjusters
                  </h5>
                  <div className="rounded-xl border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Housing Type</TableHead>
                          <TableHead className="text-right">Adjustment Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {settings.housingTypeAdjusters.map((a) => (
                          <TableRow key={a.housingType}>
                            <TableCell>{housingTypeNames[a.housingType] ?? `Type ${a.housingType}`}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(a.adjustmentAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </section>
              )}

              {/* Backup Energy Costs */}
              {hasBackupCosts && (
                <section>
                  <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Backup Energy Costs
                  </h5>
                  <div className="rounded-xl border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Energy Source</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {settings.backupEnergyCosts.map((e) => (
                          <TableRow key={e.energySourceId}>
                            <TableCell>{programId === 0
                              ? (pseEnergyTypeLabels[e.energySourceId] ?? ({ 4: "Electric portion (combined)", 5: "Gas portion (combined)" } as Record<number, string>)[e.energySourceId] ?? `PSE energy type ${e.energySourceId}`)
                              : e.energySourceName}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(e.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Funding Sources Section ──────────────────────────────────────────────────

function FundingSourcesSection({
  programId,
  programYear,
  fundingSources,
  onUpdated,
}: {
  programId: number
  programYear: number
  fundingSources: ProgramFundingSource[]
  onUpdated: () => void
}) {
  const [items, setItems] = useState<ProgramFundingSource[]>(() =>
    [...fundingSources].sort((a, b) => a.sortOrder - b.sortOrder)
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setItems([...fundingSources].sort((a, b) => a.sortOrder - b.sortOrder))
  }, [fundingSources])

  const hasChanges = (() => {
    const sorted = [...fundingSources].sort((a, b) => a.sortOrder - b.sortOrder)
    if (sorted.length !== items.length) return true
    return items.some((item, i) => {
      const original = sorted[i]!
      return (
        item.fundingSourceId !== original.fundingSourceId ||
        item.effectiveDate !== original.effectiveDate
      )
    })
  })()

  const moveItem = (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= items.length) return
    const next = [...items]
    ;[next[index]!, next[swapIndex]!] = [next[swapIndex]!, next[index]!]
    setItems(next.map((item, i) => ({ ...item, sortOrder: i + 1 })))
  }

  const updateEffectiveDate = (index: number, date: string) => {
    const next = [...items]
    next[index] = { ...next[index]!, effectiveDate: date }
    setItems(next)
  }

  const removeItem = (index: number) => {
    const next = items.filter((_, i) => i !== index).map((item, i) => ({ ...item, sortOrder: i + 1 }))
    setItems(next)
  }

  const addFundingSource = (source: FundingSourceOption) => {
    const newItem: ProgramFundingSource = {
      fundingSourceId: source.id,
      name: source.name,
      contractNumber: source.contractNumber,
      isActive: true,
      sortOrder: items.length + 1,
      effectiveDate: new Date().toISOString().split("T")[0]!,
      totalBudget: 0,
      totalDisbursed: 0,
      remaining: 0,
    }
    setItems([...items, newItem])
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const request: UpdateProgramFundingSourcesRequest = {
        programYear,
        fundingSources: items.map((item) => ({
          fundingSourceId: item.fundingSourceId,
          sortOrder: item.sortOrder,
          effectiveDate: item.effectiveDate,
        })),
      }
      await api.put<void>(
        `/api/benefit-programs/${programId}/funding-sources`,
        request
      )
      toast.success("Funding sources saved")
      onUpdated()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save funding sources")
    } finally {
      setSaving(false)
    }
  }

  const assignedIds = new Set(items.map((i) => i.fundingSourceId))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconCash className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Funding Sources</h4>
          <Badge variant="secondary" className="text-xs">{items.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <AddFundingSourceDialog
            assignedIds={assignedIds}
            onAdd={addFundingSource}
          />
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <IconLoader2 className="size-4 mr-1.5 animate-spin" />
            ) : (
              <IconDeviceFloppy className="size-4 mr-1.5" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No funding sources assigned for this year.
        </div>
      ) : (
        <div className="rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contract</TableHead>
                <TableHead>Effective Date</TableHead>
                <TableHead className="text-right">Budget</TableHead>
                <TableHead className="text-right">Disbursed</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead>Utilization</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={item.fundingSourceId}>
                  <TableCell className="font-mono text-muted-foreground">{item.sortOrder}</TableCell>
                  <TableCell className="font-medium">
                    {item.name}
                    {!item.isActive && (
                      <Badge variant="outline" className="ml-1.5 text-xs text-muted-foreground">
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {item.contractNumber ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="date"
                      className="w-36 h-8 text-sm"
                      value={item.effectiveDate}
                      onChange={(e) => updateEffectiveDate(index, e.target.value)}
                    />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {item.totalBudget === 0
                      ? <span className="text-muted-foreground">Not set</span>
                      : formatCurrency(item.totalBudget)
                    }
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(item.totalDisbursed)}</TableCell>
                  <TableCell className="text-right font-mono">
                    {item.totalBudget === 0
                      ? <span className="text-muted-foreground">—</span>
                      : formatCurrency(item.remaining)
                    }
                  </TableCell>
                  <TableCell>
                    <BudgetBar disbursed={item.totalDisbursed} total={item.totalBudget} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => moveItem(index, "up")}
                        disabled={index === 0}
                      >
                        <IconArrowUp className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => moveItem(index, "down")}
                        disabled={index === items.length - 1}
                      >
                        <IconArrowDown className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-red-500 hover:text-red-700"
                        onClick={() => removeItem(index)}
                      >
                        ×
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

// ─── Add Funding Source Dialog ─────────────────────────────────────────────────

function AddFundingSourceDialog({
  assignedIds,
  onAdd,
}: {
  assignedIds: Set<number>
  onAdd: (source: FundingSourceOption) => void
}) {
  const [open, setOpen] = useState(false)
  const [available, setAvailable] = useState<FundingSourceOption[]>([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      setLoadingOptions(true)
      try {
        const all = await api.get<FundingSourceOption[]>("/api/funding-sources")
        if (!cancelled) {
          setAvailable(all.filter((s) => !assignedIds.has(s.id)))
        }
      } catch {
        // silently fail — user can retry by re-opening
      } finally {
        if (!cancelled) setLoadingOptions(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, assignedIds])

  const handleSelect = (source: FundingSourceOption) => {
    onAdd(source)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <IconPlus className="size-4 mr-1.5" />
          Add Source
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Funding Source</DialogTitle>
          <DialogDescription>
            Select a funding source to assign to this program year.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 max-h-64 overflow-y-auto space-y-1">
          {loadingOptions ? (
            <div className="flex items-center gap-2 py-4 justify-center text-muted-foreground">
              <IconLoader2 className="size-4 animate-spin" />
              Loading…
            </div>
          ) : available.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              All funding sources are already assigned.
            </p>
          ) : (
            available.map((source) => (
              <button
                key={source.id}
                className="w-full text-left rounded-lg px-3 py-2 hover:bg-muted transition-colors"
                onClick={() => handleSelect(source)}
              >
                <div className="font-medium text-sm">{source.name}</div>
                {source.contractNumber && (
                  <div className="text-xs text-muted-foreground font-mono">
                    {source.contractNumber}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
